import { gql, useQuery, useMutation } from "@apollo/client";
import { useState } from "react";

export const SEND_MESSAGE_MUTATION = gql`
  mutation SendMessage(
    $content: String!
    $conversationId: String!
    $image: [String!]
    $pdfs: [String!]
  ) {
    sendMessage(
      content: $content
      conversationId: $conversationId
      image: $image
      pdfs: $pdfs
    )
  }
`;

export const useSendMessageMutation = () => {
  const [mutate, { loading, error }] = useMutation(SEND_MESSAGE_MUTATION);

  const sendMessage = (variables: {
    content: string;
    conversationId: string;
    image?: string[];
    pdfs?: string[];
  }) => {
    return mutate({ variables });
  };

  return {
    sendMessage,
    loading,
    error,
  };
};

// CREATE a new conversation
export const CREATE_CONVERSATION_MUTATION = gql`
  mutation CreateConversation($userIds: [String!]!, $projectId: String, $proposalId: String) {
    createConversation(userIds: $userIds, projectId: $projectId, proposalId: $proposalId) {
      id
      project { id title status }
      proposal { id status }
    }
  }
`;

export const useCreateConversationMutation = () => {
  const [createConversation, { loading, error }] = useMutation(CREATE_CONVERSATION_MUTATION);

  return {
    createConversation,
    loading,
    error,
  };
};

export const GET_CONVERSATION_WITH_PROJECT_QUERY = gql`
  query GetConversationWithProject($conversationId: String!) {
    getConversationWithProject(conversationId: $conversationId) {
      conversationId 
      
      users {
        id
        firstName
        lastName
        isOnline
        lastSeen
        role
        avatarUrl
      }

      messages {
      id
      content
      pdfs
      image
      createdAt
      isRead
      sender {
        id
        userName
      }
    }

      project {
      id
      title
      status
      client {
        id
        userName
        firstName
        lastName
        isOnline
        lastSeen
        avatarUrl
      }
    }

      proposal {
        id
        description
        durationDays
        amount
        pdfs
        image
        status
        createdAt
        freelancer {
          id
          firstName
          lastName
          isOnline
          lastSeen
          avatarUrl
        }
      }

      nextCursor
      hasMore
    }
  }
`;

export const useConversationWithProjectQuery = (conversationId: string) => {
  const { data, loading, error, refetch } = useQuery(GET_CONVERSATION_WITH_PROJECT_QUERY, {
    variables: { conversationId },
    fetchPolicy: "cache-and-network",
    skip: !conversationId,
  });

  const loadOlderMessages = async (cursor?: string) => {
    const { data: newData } = await refetch({
      conversationId,
      cursor,
    });

    return newData?.getConversationWithProject;
  };

  return {
    conversation: data?.getConversationWithProject || null,
    loading,
    error,
    refetch,
    loadOlderMessages,
  };
};

export const GET_CONVERSATION_BY_USERS_AND_PROJECT = gql`
  query GetConversationByUsersAndProject($userIds: [String!]!, $projectId: String!) {
    getConversationByUsersAndProject(userIds: $userIds, projectId: $projectId) {
      id
    }
  }
`;

export const useGetConversationByUsersAndProject = (userIds: string[], projectId: string) => {
  const { data, loading, error, refetch } = useQuery(GET_CONVERSATION_BY_USERS_AND_PROJECT, {
    variables: { userIds, projectId },
    skip: !userIds?.length || !projectId,
    fetchPolicy: "network-only",
  });

  return {
    conversation: data?.getConversationByUsersAndProject || null,
    loading,
    error,
    refetch,
  };
};

export const UPDATE_PROPOSAL_STATUS_MUTATION = gql`
  mutation UpdateProposalStatus($proposalId: String!, $newStatus: ProposalStatus!) {
    updateProposalStatus(proposalId: $proposalId, newStatus: $newStatus) {
      id
      status
      description
      amount
    }
  }
`;

export const useUpdateProposalStatusMutation = () => {
  const [updateProposalStatus, { loading, error, data }] = useMutation(UPDATE_PROPOSAL_STATUS_MUTATION);

  return {
    updateProposalStatus,
    loading,
    error,
    data,
  };
};

export const GET_USER_CONVERSATIONS_QUERY = gql`
  query GetUserConversations {
    getUserConversations {
      id
      createdAt
      users {
        id
        firstName
        lastName
        userName
        avatarUrl
      }

      messages {
        id
        content
        pdfs
        image
        createdAt
        isRead
        sender {
          id
          firstName
          lastName
          userName
          avatarUrl
        }
      }

      project {
        id
        title
      }
    }
  }
`;

export const useUserConversationsQuery = () => {
  const { data, loading, error, refetch } = useQuery(GET_USER_CONVERSATIONS_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  return {
    conversations: data?.getUserConversations || [],
    loading,
    error,
    refetch,
  };
};

export const ATTACH_PROJECT_AND_PROPOSAL = gql`
  mutation AttachProjectAndProposal(
    $conversationId: String!
    $projectId: String!
    $proposalId: String!
  ) {
    attachProjectAndProposal(
      conversationId: $conversationId
      projectId: $projectId
      proposalId: $proposalId
    ) {
      id
      project {
        id
        title
      }
      proposal {
        id
        status
      }
    }
  }
`;

export const useAttachProjectAndProposal = () => {
  return useMutation(ATTACH_PROJECT_AND_PROPOSAL);
};
